import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { adminProductionsService } from '../services/adminProductions.service'
import type { ProductionType } from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  applicationId: string
  applicationTitle: string
  /** Called after the production is created and the bidirectional link is written. */
  onProvisioned: (productionId: string) => void
}

const TYPE_OPTIONS: Array<{ value: ProductionType; label: string }> = [
  { value: 'showcase', label: 'Showcase' },
  { value: 'festival', label: 'Festival' },
  { value: 'film_screening', label: 'Film Screening' },
  { value: 'general', label: 'General' },
]

/**
 * Inline production creation triggered from an application edit page. Captures
 * just enough to bootstrap — full editing happens in the Command Center after.
 * The service layer handles the bidirectional link (production.intakeApplicationId
 * + application.productionId) in a single create() call.
 */
export function ProvisionProductionDialog({
  open,
  onOpenChange,
  applicationId,
  applicationTitle,
  onProvisioned,
}: Props) {
  const navigate = useNavigate()
  const [name, setName] = useState(applicationTitle)
  const [type, setType] = useState<ProductionType>('showcase')
  const [venue, setVenue] = useState('')
  const [startDate, setStartDate] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset when reopened so a fresh application title shows after navigation
  useEffect(() => {
    if (open) {
      setName(applicationTitle)
      setType('showcase')
      setVenue('')
      setStartDate('')
      setError(null)
    }
  }, [open, applicationTitle])

  async function submit() {
    setError(null)
    if (!name.trim()) return setError(withErrorCode('Name is required.', 'app/validation'))
    if (!venue.trim()) return setError(withErrorCode('Venue is required.', 'app/validation'))
    if (!startDate) return setError(withErrorCode('Start date is required.', 'app/validation'))

    setSubmitting(true)
    try {
      const slug = adminProductionsService.slugify(name)
      const id = await adminProductionsService.create({
        name: name.trim(),
        slug,
        type,
        status: 'planning',
        venue: venue.trim(),
        startDate,
        endDate: '',
        driveLink: '',
        intakeApplicationId: applicationId,
        techApplicationId: '',
      })
      onProvisioned(id)
      onOpenChange(false)
      navigate(`/admin/productions/${id}`)
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to create production.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Provision Production</DialogTitle>
          <DialogDescription>
            Creates a new production linked to this intake application. You'll be taken
            to its Command Center next.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="prov-name">Production Name</Label>
            <Input
              id="prov-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="SHIFT 2026"
            />
          </div>
          <div className="space-y-1">
            <Label>Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as ProductionType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="prov-venue">Venue</Label>
            <Input
              id="prov-venue"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              placeholder="Joyce Theater"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="prov-start">Start Date</Label>
            <Input
              id="prov-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <p className="text-xs text-muted-foreground">
            Slug, end date, Drive link, and other fields can be edited from the production
            after it's created.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Create Production
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
